/**
 * Mail 配信先グループ（mail_groups）の hooks。
 * 仕様書: docs/01_spec/spec_mail_system.md B-11
 *
 * 双方向同期方針（Phase 19 ハイブリッド方式）:
 *   - mail_groups.memberContactIds[] と mail_contacts.groupIds[] を常に対称に保つ
 *   - 書込（create / addMembers / removeMember）は WriteBatch で 1 コミットに集約
 *   - 削除整合性は Firestore Trigger（functions 側）で保証
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  arrayRemove,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore';

import type { MailGroup } from '@goenhub/shared';

import { useAuth } from '@/hooks/useAuth';
import { db } from '@/lib/firebase';

export interface MailGroupRecord extends MailGroup {
  id: string;
}

export function useMailGroups() {
  return useQuery({
    queryKey: ['mail_groups'],
    queryFn: async (): Promise<MailGroupRecord[]> => {
      const snap = await getDocs(
        query(collection(db, 'mail_groups'), orderBy('updatedAt', 'desc')),
      );
      return snap.docs.map((d) => ({ id: d.id, ...(d.data() as MailGroup) }));
    },
  });
}

interface CreateGroupArgs {
  name: string;
  description?: string;
  type: MailGroup['type'];
  memberContactIds: string[];
  keywords?: string[];
}

/**
 * グループ作成 + 全メンバー contact の groupIds を一括更新（バッチ）。
 * バッチは 500 op 制限のため、450 件単位でチャンク分割。
 */
export function useCreateMailGroup() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (data: CreateGroupArgs) => {
      const groupRef = doc(collection(db, 'mail_groups'));
      const memberIds = Array.from(new Set(data.memberContactIds));

      // 最初のバッチで group 自体を作成
      const firstBatch = writeBatch(db);
      firstBatch.set(groupRef, {
        name: data.name,
        description: data.description ?? null,
        type: data.type,
        keywords: data.keywords ?? null,
        memberContactIds: memberIds,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: user?.uid ?? '',
      });
      // 1 ops 消費。残り 449 op で contacts を更新。
      const firstChunkSize = Math.min(memberIds.length, 449);
      for (let i = 0; i < firstChunkSize; i++) {
        firstBatch.update(doc(db, 'mail_contacts', memberIds[i]), {
          groupIds: arrayUnion(groupRef.id),
          updatedAt: serverTimestamp(),
        });
      }
      await firstBatch.commit();

      // 残りの contacts を 450 件ずつバッチで更新
      for (let i = firstChunkSize; i < memberIds.length; i += 450) {
        const batch = writeBatch(db);
        const chunk = memberIds.slice(i, i + 450);
        for (const cid of chunk) {
          batch.update(doc(db, 'mail_contacts', cid), {
            groupIds: arrayUnion(groupRef.id),
            updatedAt: serverTimestamp(),
          });
        }
        await batch.commit();
      }
      return groupRef.id;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mail_groups'] });
      qc.invalidateQueries({ queryKey: ['mail_contacts'] });
    },
  });
}

export function useUpdateMailGroupMeta() {
  /** メタデータ（name / description）のみ更新。メンバー変更は別 hook を使うこと。 */
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      id: string;
      data: Partial<Pick<MailGroup, 'name' | 'description'>>;
    }) => {
      const batch = writeBatch(db);
      batch.update(doc(db, 'mail_groups', args.id), {
        ...args.data,
        updatedAt: serverTimestamp(),
      });
      await batch.commit();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mail_groups'] }),
  });
}

/** 単一メンバー追加（group 側 + contact 側を 1 バッチで更新） */
export function useAddMemberToGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { groupId: string; contactId: string }) => {
      const batch = writeBatch(db);
      batch.update(doc(db, 'mail_groups', args.groupId), {
        memberContactIds: arrayUnion(args.contactId),
        updatedAt: serverTimestamp(),
      });
      batch.update(doc(db, 'mail_contacts', args.contactId), {
        groupIds: arrayUnion(args.groupId),
        updatedAt: serverTimestamp(),
      });
      await batch.commit();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mail_groups'] });
      qc.invalidateQueries({ queryKey: ['mail_contacts'] });
    },
  });
}

/** 複数メンバー追加（チャンク分割で大量メンバーにも対応） */
export function useAddMembersToGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { groupId: string; contactIds: string[] }) => {
      const ids = Array.from(new Set(args.contactIds));
      if (ids.length === 0) return;

      // 最初のバッチで group を更新（1 op）+ contacts を 449 件まで更新
      const firstBatch = writeBatch(db);
      firstBatch.update(doc(db, 'mail_groups', args.groupId), {
        memberContactIds: arrayUnion(...ids), // arrayUnion はマルチ引数で重複排除
        updatedAt: serverTimestamp(),
      });
      const firstChunkSize = Math.min(ids.length, 449);
      for (let i = 0; i < firstChunkSize; i++) {
        firstBatch.update(doc(db, 'mail_contacts', ids[i]), {
          groupIds: arrayUnion(args.groupId),
          updatedAt: serverTimestamp(),
        });
      }
      await firstBatch.commit();

      for (let i = firstChunkSize; i < ids.length; i += 450) {
        const batch = writeBatch(db);
        const chunk = ids.slice(i, i + 450);
        for (const cid of chunk) {
          batch.update(doc(db, 'mail_contacts', cid), {
            groupIds: arrayUnion(args.groupId),
            updatedAt: serverTimestamp(),
          });
        }
        await batch.commit();
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mail_groups'] });
      qc.invalidateQueries({ queryKey: ['mail_contacts'] });
    },
  });
}

/** メンバー除外（group 側 + contact 側を 1 バッチで更新） */
export function useRemoveMemberFromGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { groupId: string; contactId: string }) => {
      const batch = writeBatch(db);
      batch.update(doc(db, 'mail_groups', args.groupId), {
        memberContactIds: arrayRemove(args.contactId),
        updatedAt: serverTimestamp(),
      });
      batch.update(doc(db, 'mail_contacts', args.contactId), {
        groupIds: arrayRemove(args.groupId),
        updatedAt: serverTimestamp(),
      });
      await batch.commit();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mail_groups'] });
      qc.invalidateQueries({ queryKey: ['mail_contacts'] });
    },
  });
}

/**
 * グループ削除：group doc を削除するのみ。
 * 各メンバー contact から groupId を消す処理は Firestore Trigger
 * （functions/src/mail/groupSync.ts の onMailGroupDeleted）が担当。
 */
export function useDeleteMailGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await deleteDoc(doc(db, 'mail_groups', id));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mail_groups'] });
      qc.invalidateQueries({ queryKey: ['mail_contacts'] });
    },
  });
}
